import type { SignupErrorData } from "@constructionautomation/backend/types/signup";

/** Structured company-creation/invitation error from the backend `ConvexError`, if any. */
export function getSignupError(error: unknown): SignupErrorData | null {
  if (typeof error !== "object" || error === null || !("data" in error)) return null;
  const data = error.data;
  if (typeof data !== "object" || data === null || !("code" in data) || !("message" in data)) return null;
  if (typeof data.code !== "string" || typeof data.message !== "string") return null;
  return data as SignupErrorData;
}

export function getSignupErrorMessage(error: unknown, fallback: string): string {
  return getSignupError(error)?.message ?? (error instanceof Error ? error.message : fallback);
}

const ROLE_DESCRIPTIONS: Record<string, string> = {
  owner: "Owners create projects, process work, and manage members and billing.",
  admin: "Admins create projects, process work, and manage members and billing.",
  member: "Members upload documents, process work, and edit results on assigned projects.",
  reviewer: "Reviewers have read-only access: they can view assigned projects and download completed exports.",
};

/** What an invited role can do, in plain language; null for roles we don't describe. */
export function describeRoleSlug(roleSlug: string | null): string | null {
  return ROLE_DESCRIPTIONS[roleSlug ?? "member"] ?? null;
}

/** WorkOS role slugs as people read them; unknown slugs (e.g. future roles) are title-cased. */
export function formatRoleSlug(roleSlug: string | null): string {
  if (!roleSlug) return "Member";
  return roleSlug
    .split(/[-_\s]+/)
    .filter(Boolean)
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(" ");
}
